// Reach Tool — estimate campaign reach and frequency across channels.
//
// The app shell: workspace state, navigation, and the tab wiring. Every page
// lives under pages/, shared pieces under components/, hooks.js and format.js —
// the module layout the rest of the repo uses.

import { useCallback, useEffect, useRef, useState } from 'react';
import OrbitModuleHeader from '../../shared/hub/OrbitModuleHeader.jsx';
import { useWorkspace } from '../../shared/workspace/WorkspaceContext.jsx';
import { reachApi } from './api.js';
import { BackButton, LoadingState, T, defaultStreamName } from './components/ui.jsx';
import { ConnectionsView } from './pages/ConnectionsPage.jsx';
import { ReachExtractorTab } from './pages/ExtractorWizard.jsx';
import { DataStreamsTab } from './pages/StreamsPage.jsx';
import { AnalyzeActTab, INITIAL_GCP_CONNECTIONS } from './pages/AnalyzePage.jsx';
import { historyExportId, historyPath } from './exportHistory.js';
import './reach-tool.css';

const NAV = [
  { id: 'connect-mix', label: 'Connect & Mix' },
  { id: 'analyze-act', label: 'Analyze & Act' },
];

const CONNECT_MIX_SECTIONS = [
  { id: 'streams', label: 'Data streams' },
  { id: 'connections', label: 'Connections' },
];


const ANALYZE_ACT_SECTIONS = [
  { id: 'pivot-tables', label: 'Pivot tables' },
  { id: 'exports', label: 'Exports' },
];


// ── URL routing ──────────────────────────────────────────────────────────────
//
// Every page has a URL — /reach-tool, /reach-tool/connect-mix/<section>,
// /reach-tool/analyze-act/<section> — so a reload lands back on the page being
// looked at and Back walks through pages actually visited. Same hand-rolled
// pattern as Orbit Development: parse the path, listen for popstate, and have
// navigation push a URL rather than set state directly.

const BASE = '/reach-tool';

const SECTIONS_BY_TAB = {
  'connect-mix': CONNECT_MIX_SECTIONS,
  'analyze-act': ANALYZE_ACT_SECTIONS,
};

const DEFAULT_SECTION = { 'connect-mix': 'streams', 'analyze-act': 'pivot-tables' };

// pushState alone updates the URL without telling anyone. App.jsx's router and the
// workspace provider both listen for popstate, so the synthetic event is what navigates.
function navigate(path) {
  history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

const sectionPath = (tab, section) => `${BASE}/${tab}/${section}`;

// An unknown group or section falls back to a page that exists rather than rendering
// nothing: a stale bookmark should degrade to the nearest real page, not a blank screen.
function parseReachPath() {
  const p = window.location.pathname;
  const rest = p.startsWith(BASE) ? p.slice(BASE.length).replace(/^\//, '') : '';
  const [group, section] = rest.split('/');
  const sections = SECTIONS_BY_TAB[group];
  if (sections) {
    return {
      tab: group,
      section: sections.some(s => s.id === section) ? section : DEFAULT_SECTION[group],
      exportHistoryId: historyExportId(p),
    };
  }
  return { tab: 'connect-mix', section: DEFAULT_SECTION['connect-mix'] };
}


// Section switcher under the top bar. Counts render only when there is something to count —
// a zero next to every label is noise, not information.
function ReachSubTabs({ sections, active, counts = {}, onSelect }) {
  return (
    <nav className="o-subtabs">
      {sections.map(section => (
        <button key={section.id} type="button" className="o-subtab"
                aria-current={active === section.id ? 'page' : 'false'}
                onClick={() => onSelect(section.id)}>
          {section.label}
          {counts[section.id] > 0 && <span className="o-add o-num">{counts[section.id]}</span>}
        </button>
      ))}
    </nav>
  );
}


function ConnectMixTab({ section = 'streams', streams, calculatedFields, onSaveCalculatedField, onUpdateStream, onRunStream, onSaveStream, onDeleteStream, onDuplicateStream, onSavePivot, onPivotSaved, streamsState = 'ready', streamsError = '', onRetryLoad }) {
  const [mode, setMode]       = useState('list'); // 'list' | 'extractor'

  return (
    <div style={{ minWidth: 0 }}>
        {section === 'streams' && mode === 'list' && (
          <DataStreamsTab streams={streams} calculatedFields={calculatedFields} onSaveCalculatedField={onSaveCalculatedField} onUpdateStream={onUpdateStream} onRunStream={onRunStream} onDeleteStream={onDeleteStream} onDuplicateStream={onDuplicateStream} onGoToExtractor={() => setMode('extractor')} onSavePivot={onSavePivot} onPivotSaved={onPivotSaved} streamsState={streamsState} streamsError={streamsError} onRetryLoad={onRetryLoad} />
        )}
        {section === 'streams' && mode === 'extractor' && (
          <div>
            <BackButton onClick={() => setMode('list')} />
            {/* A successful create returns to the refreshed list. Failures remain in the wizard,
                beside the fields that need attention. */}
            <ReachExtractorTab streams={streams} onSaveStream={onSaveStream} onCreated={() => setMode('list')} onCancel={() => setMode('list')} />
          </div>
        )}
        {section === 'connections' && <ConnectionsView />}
    </div>
  );
}


// Streams are configuration the batch layer reads, so they live in bp_connectors and are
// fetched from the API. There is deliberately no seeded fallback: a stream that existed only in
// this browser would show in the list, accept a schedule and never run.
const INITIAL_STREAMS = [];


const REACH_WORKSPACE_STORAGE_PREFIX = 'reach-tool-workspace:';

const REACH_WORKSPACE_MIGRATION_KEY = 'reach-tool-workspace-migration-complete';


const cloneReachData = value => JSON.parse(JSON.stringify(value));

const reachWorkspaceStorageKey = workspaceId => `${REACH_WORKSPACE_STORAGE_PREFIX}${workspaceId}`;


function normalizeReachStreamIds(data) {
  const streams = Array.isArray(data.streams) ? data.streams : [];
  const reservedIds = new Set(streams
    .map(stream => Number(stream.id))
    .filter(id => Number.isInteger(id) && id > 0));
  const assignedIds = new Set();
  const idMap = new Map();
  let candidateId = 1;

  const normalizedStreams = streams.map(stream => {
    const numericId = Number(stream.id);
    let id = Number.isInteger(numericId) && numericId > 0 && !assignedIds.has(numericId)
      ? numericId
      : null;

    if (id === null) {
      while (reservedIds.has(candidateId) || assignedIds.has(candidateId)) candidateId += 1;
      id = candidateId;
      candidateId += 1;
    }

    assignedIds.add(id);
    idMap.set(stream.id, id);
    return stream.id === id ? stream : { ...stream, id };
  });

  const remapStreamReferences = items => (Array.isArray(items) ? items.map(item => ({
    ...item,
    sourceStreamIds: Array.isArray(item.sourceStreamIds)
      ? item.sourceStreamIds.map(id => idMap.get(id) ?? id)
      : item.sourceStreamIds,
  })) : items);

  return {
    ...data,
    streams: normalizedStreams,
    savedPivots: remapStreamReferences(data.savedPivots),
    savedExports: remapStreamReferences(data.savedExports),
    exportHistory: remapStreamReferences(data.exportHistory),
  };
}


function createDefaultReachWorkspaceData() {
  return {
    streams: cloneReachData(INITIAL_STREAMS),
    calculatedFields: [],
    savedPivots: [],
    savedExports: [],
    exportHistory: [],
    gcpConnections: cloneReachData(INITIAL_GCP_CONNECTIONS),
  };
}


function readStoredArray(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null');
    return Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}


function loadReachWorkspaceData(workspaceId) {
  const defaults = createDefaultReachWorkspaceData();
  const storageKey = reachWorkspaceStorageKey(workspaceId);

  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (stored && typeof stored === 'object') {
      return normalizeReachStreamIds(Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => [key, Array.isArray(stored[key]) ? stored[key] : fallback])));
    }

    const firstWorkspace = !localStorage.getItem(REACH_WORKSPACE_MIGRATION_KEY);
    const data = firstWorkspace ? {
      ...defaults,
      calculatedFields: readStoredArray('reach-tool-calculated-fields') ?? defaults.calculatedFields,
      savedPivots: readStoredArray('reach-tool-saved-pivots') ?? defaults.savedPivots,
      savedExports: readStoredArray('reach-tool-saved-exports') ?? defaults.savedExports,
      exportHistory: readStoredArray('reach-tool-export-history') ?? defaults.exportHistory,
    } : defaults;

    localStorage.setItem(storageKey, JSON.stringify(data));
    if (firstWorkspace) localStorage.setItem(REACH_WORKSPACE_MIGRATION_KEY, String(workspaceId));
    return normalizeReachStreamIds(data);
  } catch {
    return defaults;
  }
}


function ReachWorkspaceApp({ workspaceId }) {
  const [route, setRoute] = useState(parseReachPath);
  const [connectMixResetKey, setConnectMixResetKey] = useState(0);

  useEffect(() => {
    const onPop = () => setRoute(parseReachPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const { tab } = route;
  const connectSection = tab === 'connect-mix' ? route.section : DEFAULT_SECTION['connect-mix'];
  const analyzeSection = tab === 'analyze-act' ? route.section : DEFAULT_SECTION['analyze-act'];

  // The section each group was last on, so switching top-level tabs returns to it rather
  // than resetting to the first section — matching what the stateful nav did before URLs.
  const lastSections = useRef({ ...DEFAULT_SECTION });
  useEffect(() => {
    if (route.section) lastSections.current[route.tab] = route.section;
  }, [route]);

  const changeTab = (id) => {
    if (id === tab) return;
    navigate(sectionPath(id, lastSections.current[id]));
  };
  const [workspaceSeed] = useState(() => loadReachWorkspaceData(workspaceId));

  // Streams come from the server (bp_connectors), not from the workspace seed. They are
  // configuration the batch-processing layer reads to decide what to extract, so localStorage
  // cannot be their home — a stream that exists only in this browser would never run. The
  // other collections below are analysis artefacts with no backend and stay local.
  const [streams, setStreams] = useState([]);
  const [streamsState, setStreamsState] = useState('loading'); // loading | ready | error
  const [streamsError, setStreamsError] = useState('');
  // Server-side since bp_pivots/bp_calculated_fields (migration 948). Empty until loaded —
  // never seeded from localStorage, which was per-browser "persistence" that a cache clear
  // deleted. The seed below is read once, for the one-time push-up migration only.
  const [calculatedFields, setCalculatedFields] = useState([]);
  const [savedPivots, setSavedPivots] = useState([]);
  const [analysisError, setAnalysisError] = useState('');
  const [savedExports, setSavedExports] = useState([]);
  const [exportHistory, setExportHistory] = useState([]);
  const [legacyExports, setLegacyExports] = useState(workspaceSeed.savedExports.map(item => ({ ...item, legacy: true, enabled: false, status: 'Local draft' })));
  const [exportsError, setExportsError] = useState('');
  const [gcpConnections, setGcpConnections] = useState([]);

  // One-time migration of pre-server artefacts. Fields go up first so their new server
  // UUIDs can replace the old client-side calc_* ids everywhere that referenced them —
  // pivot definitions and the still-local exports — otherwise every migrated pivot would
  // point at field ids that no longer exist. Flagged per workspace so a later deliberate
  // deletion is not "helpfully" undone by re-migration.
  const migrateLocalAnalysis = useCallback(async () => {
    const flag = `reach-tool-server-migrated:${workspaceId}`;
    const localFields = workspaceSeed.calculatedFields || [];
    const localPivots = workspaceSeed.savedPivots || [];
    if (localStorage.getItem(flag) || (!localFields.length && !localPivots.length)) return false;

    const idMap = new Map();
    for (const field of localFields) {
      const saved = await reachApi.createCalculatedField(field);
      idMap.set(field.id, saved.id);
    }
    const remap = ids => (ids || []).map(id => idMap.get(id) || id);
    for (const pivot of localPivots) {
      await reachApi.createPivot({
        ...pivot,
        dimensions: remap(pivot.dimensions),
        metrics: remap(pivot.metrics),
      });
    }
    setLegacyExports(current => current.map(item => ({
      ...item,
      dimensions: remap(item.dimensions),
      metrics: remap(item.metrics),
    })));
    localStorage.setItem(flag, new Date().toISOString());
    return true;
  }, [workspaceId, workspaceSeed]);

  const loadAnalysis = useCallback(async () => {
    try {
      setAnalysisError('');
      let [pivots, fields] = await Promise.all([
        reachApi.listPivots(), reachApi.listCalculatedFields()]);
      // Migrate only INTO an empty server: a second browser holding its own stale copy must
      // not duplicate what a colleague's already pushed. Re-read afterwards so what renders
      // is exactly what the server now holds, ids included.
      if (!pivots.length && !fields.length && await migrateLocalAnalysis()) {
        [pivots, fields] = await Promise.all([
          reachApi.listPivots(), reachApi.listCalculatedFields()]);
      }
      setSavedPivots(pivots);
      setCalculatedFields(fields);
    } catch (error) {
      setAnalysisError(error?.message || 'Saved pivots and calculated fields could not be loaded.');
    }
  }, [migrateLocalAnalysis]);

  useEffect(() => { loadAnalysis(); }, [loadAnalysis, workspaceId]);

  const loadExports = useCallback(async () => {
    try {
      const [items, history] = await Promise.all([reachApi.listExports(), reachApi.exportHistory()]);
      setSavedExports(items);
      setExportHistory(history);
      setExportsError('');
    } catch (error) { setExportsError(error?.message || 'Exports could not be loaded.'); }
  }, []);
  useEffect(() => {
    if (analyzeSection !== 'exports' || tab !== 'analyze-act') return undefined;
    loadExports();
    if (route.exportHistoryId) return undefined;
    const timer = window.setInterval(() => { if (!document.hidden) loadExports(); }, 5000);
    return () => window.clearInterval(timer);
  }, [tab, analyzeSection, route.exportHistoryId, loadExports]);

  const loadStreams = useCallback(async () => {
    setStreamsState('loading');
    try {
      setStreams(await reachApi.listStreams());
      setStreamsError('');
      setStreamsState('ready');
    } catch (error) {
      setStreamsError(error?.message || 'Could not load data streams.');
      setStreamsState('error');
    }
  }, []);

  // Reloads when the workspace changes: the API client reads the active client from storage
  // per request, so switching workspace must re-fetch rather than re-filter what is held.
  useEffect(() => { loadStreams(); }, [loadStreams, workspaceId]);

  // Refresh a "quiet" reload — no loading state, so an in-flight poll never blanks the table
  // the user is reading.
  const refreshStreams = useCallback(async () => {
    try {
      setStreams(await reachApi.listStreams());
      setStreamsError('');
    } catch {
      // A failed poll is not worth surfacing: the previous rows are still valid and the next
      // tick will correct them. Only an explicit load reports an error.
    }
  }, []);

  // Poll while anything is in flight or waiting for its schedule clock, and stop as soon as
  // every transient state has settled.
  //
  // Triggering a run reloads immediately, but that lands milliseconds after the row is
  // enqueued — so it reads "Queued" even though the batch layer picks it up a moment later.
  // Without this the status never moved again until a manual refresh, which made a working
  // extraction look stuck.
  //
  // Saving an edit clears nextRunAt and asks the batch app to recompute it immediately. That
  // wake-up is intentionally fail-soft, though: a cold batch host can outlive the backend's
  // short timeout and the five-minute scheduler timer then performs the same calculation.
  // Treating "Scheduling…" as transient keeps the row fresh across both paths instead of
  // leaving the browser on the null value returned by the save until a manual refresh.
  //
  // Only runs when there is something to watch, so an idle workspace makes no requests at all.
  const hasTransientStreamState = streams.some(stream =>
    stream.runStatus === 'Queued'
    || stream.runStatus === 'Processing'
    || (stream.enabled && !stream.nextRunAt && !stream.scheduleError));

  useEffect(() => {
    if (!hasTransientStreamState) return undefined;
    const timer = setInterval(refreshStreams, 3000);
    return () => clearInterval(timer);
  }, [hasTransientStreamState, refreshStreams]);

  // Only the collections that still have no backend. Pivots and calculated fields live
  // server-side now and deliberately do NOT round-trip through here — writing them back
  // would resurrect the per-browser copies the migration just retired.
  //
  // MERGED into the stored object, never a replacement: the legacy calculatedFields and
  // savedPivots keys must survive in storage until the one-time migration has pushed them
  // up. This effect fires on mount — before the async migration finishes — and a plain
  // setItem here would destroy the migration's source data on the first visit where the
  // backend happened to be unreachable, with the not-yet-set flag ensuring nobody retried.
  useEffect(() => {
    const storageKey = reachWorkspaceStorageKey(workspaceId);
    let existing = {};
    try { existing = JSON.parse(localStorage.getItem(storageKey) || '{}') || {}; } catch { existing = {}; }
    // Retire the old, unverified local GCP placeholders. Never persist credentials or
    // server connection metadata in browser storage; the wizard loads it from the API.
    delete existing.gcpConnections;
    localStorage.setItem(storageKey, JSON.stringify({
      ...existing,
      // Keep old browser-only drafts until they are explicitly reconnected and saved.
      // Server definitions and delivery history are never written to localStorage.
      savedExports: legacyExports,
    }));
  }, [workspaceId, legacyExports]);

  const addStream = async (streamOrStreams) => {
    const incomingStreams = Array.isArray(streamOrStreams) ? streamOrStreams : [streamOrStreams];
    const createdAccounts = [];
    try {
      // Sequential rather than Promise.all: the wizard creates at most a handful at a time,
      // and one failure part-way through should leave the successful ones created rather than
      // racing an unknown number of writes against a server error.
      for (const stream of incomingStreams) {
        await reachApi.createStream({
          name: defaultStreamName(stream.platform, stream.cadence),
          enabled: true,
          ...stream,
        });
        createdAccounts.push(...(stream.adAccounts || []));
      }
    } catch (error) {
      error.createdAccounts = createdAccounts;
      throw error;
    } finally {
      // Whatever happened, show what exists now: a mid-list failure leaves the earlier
      // streams created, and the list must say so. Quiet refresh — the error belongs to the
      // wizard, which catches the rethrow; poisoning the list's own state over a save that
      // failed elsewhere made a working screen look broken.
      await refreshStreams();
    }
  };

  const updateStream = async (id, updated) => {
    const saved = await reachApi.updateStream(id, updated);
    setStreams(current => current.map(item => item.id === id ? saved : item));
    return saved;
  };

  // Throws to the caller: deletion is invoked from the list's own toolbar, which shows the
  // failure next to the button that caused it rather than poisoning the list's load state.
  const deleteStream = async (id) => {
    await reachApi.deleteStream(id);
    await refreshStreams();
  };

  // A duplicate starts DISABLED, deliberately: an enabled copy would extract the same
  // account on the same schedule the moment the scheduler saw it, doubling API pressure
  // before the user has changed the one thing they duplicated it to change. Disabled also
  // means the create route skips the automatic backfill — the original already pulled it.
  const duplicateStream = async (stream) => {
    await reachApi.createStream({
      ...stream,
      name: `${stream.name} (copy)`,
      enabled: false,
    });
    await refreshStreams();
  };

  // Force trigger / reprocess. Queues the work and returns — extraction can take up to half an
  // hour, so this reloads the list to show the new status rather than waiting for a result.
  const runStream = async (id, range) => {
    const result = await reachApi.runStream(id, range);
    await loadStreams();
    return result;
  };

  // Create-or-update decided by whether the id is one the server gave us: an existing field
  // arrives from the edit dialog carrying its UUID; a new one carries the dialog's throwaway
  // client id, which the server replaces. Returns the SAVED field — callers that immediately
  // reference the id (the pivot adds a fresh field to its columns) must use the returned one.
  const saveCalculatedField = async (field) => {
    try {
      const exists = calculatedFields.some(item => item.id === field.id);
      const saved = exists
        ? await reachApi.updateCalculatedField(field.id, field)
        : await reachApi.createCalculatedField(field);
      setCalculatedFields(current => exists
        ? current.map(item => (item.id === saved.id ? saved : item))
        : [saved, ...current]);
      return saved;
    } catch (error) {
      setAnalysisError(error?.message || 'The calculated field could not be saved.');
      return null;
    }
  };

  const deleteCalculatedField = async (id) => {
    try {
      await reachApi.deleteCalculatedField(id);
      setCalculatedFields(current => current.filter(field => field.id !== id));
    } catch (error) {
      setAnalysisError(error?.message || 'The calculated field could not be deleted.');
    }
  };

  const savePivot = async (pivot) => {
    const exists = savedPivots.some(item => item.id === pivot.id);
    const saved = exists ? await reachApi.updatePivot(pivot.id, pivot) : await reachApi.createPivot(pivot);
    setSavedPivots(current => [saved, ...current.filter(item => item.id !== saved.id)]);
    return saved;
  };

  const deletePivot = async (id) => {
    try {
      await reachApi.deletePivot(id);
      setSavedPivots(current => current.filter(pivot => pivot.id !== id));
    } catch (error) {
      setAnalysisError(error?.message || 'The pivot could not be deleted.');
    }
  };

  const saveExport = async (itemOrItems) => {
    const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
    const saved = await reachApi.saveExports(items);
    setSavedExports(current => [...saved, ...current]);
    return saved;
  };

  const deleteExport = async (id) => {
    if (legacyExports.some(item => item.id === id)) { setLegacyExports(current => current.filter(item => item.id !== id)); return; }
    try { await reachApi.deleteExport(id); await loadExports(); }
    catch (error) { setExportsError(error.message); }
  };

  const updateExport = async (id, changes) => {
    const legacy = legacyExports.find(item => item.id === id);
    const item = legacy || savedExports.find(item => item.id === id);
    if (!item) throw new Error('This export is no longer available. Refresh the list.');
    if ('status' in changes && !changes.name) {
      if (legacy) { setExportsError('Edit this local draft and save it to the server before scheduling it.'); return; }
      try { await reachApi.setExportEnabled(id, changes.status !== 'Paused'); await loadExports(); }
      catch (error) { setExportsError(error.message); }
      return;
    }
    const definitions = Array.isArray(changes) ? changes : [{ ...item, ...changes }];
    const saved = await reachApi.saveExports(definitions, legacy ? undefined : id);
    setSavedExports(current => [...saved, ...current.filter(value => value.id !== id)]);
    if (legacy) setLegacyExports(current => current.filter(value => value.id !== id));
  };

  const cloneExport = async (item) => {
    const name = `${item.baseName || item.name} copy`;
    try { await saveExport({ ...item, name, baseName: name, enabled: false }); }
    catch (error) { setExportsError(error.message); }
  };

  const runExport = async (item) => {
    if (item.legacy) { setExportsError('Edit this local draft and save it to the server before running it.'); return; }
    try { await reachApi.runExport(item.id); await loadExports(); }
    catch (error) { setExportsError(error.message); }
  };

  // Re-selecting the active Data streams section remounts ConnectMixTab, which is how the
  // extractor wizard gets dismissed back to the list without its own nav entry. No URL push
  // in that case — the page is the same page, and a duplicate history entry would make Back
  // appear to do nothing.
  const selectConnectSection = (section) => {
    if (connectSection === section) setConnectMixResetKey(current => current + 1);
    else navigate(sectionPath('connect-mix', section));
  };

  const selectAnalyzeSection = (section) => {
    if (analyzeSection !== section || route.exportHistoryId) navigate(sectionPath('analyze-act', section));
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      <style>{`
        .reach-pivot-head { flex-wrap: wrap; }
        .reach-choice-grid > * { min-width: 220px; }
        .reach-stream-stepper { overflow-x: auto; padding-bottom: 8px; }
        .o-tbl td { white-space: normal !important; overflow-wrap: anywhere; line-height: 1.35; }
        .o-tbl td > .o-mono { white-space: normal !important; overflow-wrap: anywhere; }

        @media (max-width: 1100px) {
          .reach-pivot-layout { grid-template-columns: minmax(0, 1fr) !important; }
          .reach-form-grid-3 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }

        @media (max-width: 760px) {
          .reach-form-grid-2,
          .reach-form-grid-3,
          .reach-filter-layout,
          .reach-range-layout,
          .reach-stepper { grid-template-columns: minmax(0, 1fr) !important; }
          .reach-range-presets { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .reach-filter-rail { border-right: 0 !important; border-bottom: 1px solid var(--rule); }
          .reach-choice-grid { flex-wrap: wrap; }
          .reach-choice-grid > * { flex-basis: 100%; min-width: 0; }
          .reach-stepper { gap: 8px; }
        }
      `}</style>
      <OrbitModuleHeader title="Reach Tool" moduleId="reach-tool" tabs={NAV} activeTab={tab} onTabChange={changeTab} />

      <main className="o-page">
        {/* Analysis-artefact failures (pivots, calculated fields) are workspace-level: they
            can strike from any screen that saves or loads them, so they surface here once
            rather than being re-implemented per view. */}
        {analysisError && <div className="o-state o-error" style={{ marginBottom: 16 }}>{analysisError}</div>}
        {tab === 'connect-mix' && (
          <>
            <ReachSubTabs sections={CONNECT_MIX_SECTIONS} active={connectSection}
                          counts={{ streams: streams.length }} onSelect={selectConnectSection} />
            <ConnectMixTab key={`${workspaceId}-${connectSection}-${connectMixResetKey}`} section={connectSection} streams={streams} calculatedFields={calculatedFields} onSaveCalculatedField={saveCalculatedField} onUpdateStream={updateStream} onRunStream={runStream} onSaveStream={addStream} onDeleteStream={deleteStream} onDuplicateStream={duplicateStream} onSavePivot={savePivot} onPivotSaved={() => navigate(sectionPath('analyze-act', 'pivot-tables'))} streamsState={streamsState} streamsError={streamsError} onRetryLoad={loadStreams} />
          </>
        )}
        {tab === 'analyze-act' && (
          <>
            <ReachSubTabs sections={ANALYZE_ACT_SECTIONS} active={analyzeSection}
                          counts={{ 'pivot-tables': savedPivots.length, exports: savedExports.length }} onSelect={selectAnalyzeSection} />
            {analyzeSection === 'exports' && !route.exportHistoryId && exportsError && <div className="o-state o-error" role="alert">{exportsError}<button type="button" className="o-btn o-btn-sm" onClick={loadExports}>Retry</button></div>}
            {analyzeSection === 'exports' && !route.exportHistoryId && legacyExports.length > 0 && <p className="o-hint"><b>Previous local drafts kept.</b> Edit each local draft, reconnect its destination and save it before running. Existing tables are not changed automatically.</p>}
            <AnalyzeActTab key={`${workspaceId}-${analyzeSection}`} section={analyzeSection} savedPivots={savedPivots} savedExports={[...savedExports, ...legacyExports]} exportHistory={exportHistory} exportHistoryId={route.exportHistoryId} onOpenExportHistory={id => navigate(historyPath(id))} onCloseExportHistory={() => navigate(sectionPath('analyze-act', 'exports'))} streams={streams} calculatedFields={calculatedFields} gcpConnections={gcpConnections} onChangeGcpConnections={setGcpConnections} onSaveCalculatedField={saveCalculatedField} onSavePivot={savePivot} onDeletePivot={deletePivot} onSaveExport={saveExport} onDeleteExport={deleteExport} onUpdateExport={updateExport} onCloneExport={cloneExport} onRunExport={runExport} />
          </>
        )}
      </main>
    </div>
  );
}


export default function ReachToolApp() {
  const { clientId, loading } = useWorkspace();
  const workspaceId = clientId ? String(clientId) : null;
  const [readyWorkspaceId, setReadyWorkspaceId] = useState(null);

  // WorkspaceContext writes the effective client header in a passive effect. On a hard refresh,
  // mounting Reach in the same render can let this module's child effects request streams and
  // pivots one effect too early, before X-Orbit-Client exists. Advancing this local gate from an
  // effect guarantees the provider's complete effect flush has synchronized request storage
  // before any Reach data screen mounts. It also protects workspace switches from one stale-ID
  // request without changing the shared provider used by the rest of Orbit.
  useEffect(() => {
    setReadyWorkspaceId(workspaceId);
  }, [workspaceId]);

  if (workspaceId && readyWorkspaceId === workspaceId) {
    return <ReachWorkspaceApp key={workspaceId} workspaceId={workspaceId} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      <OrbitModuleHeader title="Reach Tool" moduleId="reach-tool" tabs={[]} activeTab="connect-mix" onTabChange={() => {}} />
      <div className="o-page">
        {loading || workspaceId
          ? <LoadingState title="Loading workspace…" detail="Reach will open when the available workspaces are ready." style={{ maxWidth: 'none' }} />
          : <div className="o-state"><b>No workspace selected.</b> Select a workspace in the top bar to view its Reach data.</div>}
      </div>
    </div>
  );
}

